import { useState } from "react";
import { CheckCircle2, Video, MapPin } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TIMES = ["07:00 AM", "09:00 AM", "11:00 AM", "04:00 PM", "06:00 PM", "08:00 PM"];

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", time: "", mode: "Online (Video)", topic: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.date || !form.time) { toast.error("Please fill name, email, date and time."); return; }
    setBusy(true);
    try { await api.post("/bookings", form); setDone(true); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) return (
    <div data-testid="booking-success" className="min-h-screen pt-32 pb-20 text-center">
      <div className="grid place-items-center w-16 h-16 rounded-full bg-primary/10 text-primary mx-auto mb-6"><CheckCircle2 size={30} /></div>
      <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight">Consultation requested!</h1>
      <p className="text-muted-foreground mt-4 max-w-md mx-auto">Thanks {form.name.split(" ")[0]}! We'll confirm your slot for {form.date} at {form.time} via email/WhatsApp shortly.</p>
    </div>
  );

  return (
    <div data-testid="booking-page">
      <PageHeader overline="Book a Consultation" title="Book your free consult" subtitle="Pick a time that works and we'll confirm your 1:1 session with the coach." />
      <section className="mx-auto max-w-2xl px-5 py-14">
        <form onSubmit={submit} className="border border-border rounded-lg bg-card p-7 md:p-9 space-y-5" data-testid="booking-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Name</Label><Input data-testid="booking-name" value={form.name} onChange={set("name")} className="mt-2 h-12" /></div>
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Email</Label><Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} className="mt-2 h-12" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Phone</Label><Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} className="mt-2 h-12" /></div>
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Date</Label><Input data-testid="booking-date" type="date" value={form.date} onChange={set("date")} className="mt-2 h-12" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Time</Label>
              <Select value={form.time} onValueChange={set("time")}><SelectTrigger data-testid="booking-time" className="mt-2 h-12"><SelectValue placeholder="Pick a time" /></SelectTrigger>
                <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Mode</Label>
              <Select value={form.mode} onValueChange={set("mode")}><SelectTrigger data-testid="booking-mode" className="mt-2 h-12"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Online (Video)">Online (Video)</SelectItem><SelectItem value="In-person">In-person</SelectItem></SelectContent></Select>
            </div>
          </div>
          <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">What do you want to focus on?</Label><Textarea data-testid="booking-topic" rows={3} value={form.topic} onChange={set("topic")} className="mt-2" placeholder="e.g. fat loss, form check, nutrition…" /></div>
          <button data-testid="booking-submit" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform disabled:opacity-60">{busy ? "Requesting…" : "Request Consultation"}</button>
          <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground pt-1"><span className="flex items-center gap-1.5"><Video size={14} /> Google Meet / Zoom</span><span className="flex items-center gap-1.5"><MapPin size={14} /> Or in-person</span></div>
        </form>
      </section>
    </div>
  );
}
